import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import IconComponent from "@CubeComponents/icons";
import { useState, useEffect, memo, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { fileConversionStatus, exportType } from "../../../../helpers/constants";
import { showToastNotification } from "../../reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../../services/constants";
import { useTranslation } from "react-i18next";
import type {
  DeleteExportDataResponse,
  ExportScheduleTasksResponse,
  GetAllExportDataResponse,
} from "@ServerTypes/tasks/tasks.schema";
import NoData from "@Assets/icons/svg/NoData.svg";
import { useNavigate } from "react-router-dom";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";

function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

interface ExportItem {
  _id: string;
  awsKey: string;
  fileName?: string;
  exportType: string;
  createdAt: number;
  exportStatus?: string;
  scheduleId?: string;
}

interface SectionHeaderProps {
  title: string;
  buttonText?: string;
}

interface SectionContentProps {
  items: ExportItem[];
  sectionType: "ongoing" | "completed";
  onDelete?: (id: string) => void;
  onRetry?: (id: string, scheduleId: string, type: string) => void;
  onDownload?: (awsKey: string, fileName: string) => void;
}

interface ExportSectionProps {
  title: string;
  items: ExportItem[];
  buttonText?: string;
  sectionType: "ongoing" | "completed";
  onDelete?: (id: string) => void;
  onRetry?: (id: string, scheduleId: string, type: string) => void;
  onDownload?: (awsKey: string, fileName: string) => void;
}

interface ExportedFilesProps {
  onCountChange?: (count: number) => void;
}

function SectionHeader({ title, buttonText }: SectionHeaderProps) {
  return (
    <div className="flex h-[56px] items-center justify-between px-6 border-b border-[#DCE8F5] font-bold">
      <Text>{title}</Text>
      {buttonText && (
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} size="small">
          {buttonText}
        </ButtonComponent>
      )}
    </div>
  );
}

const SectionContent = memo(function SectionContent({
  items,
  sectionType,
  onDelete,
  onRetry,
  onDownload,
}: SectionContentProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      {items.map((item: any) => {
        const fileName = item.fileName || item.awsKey;
        const formattedDate = formatTimestamp(item.createdAt);
        const isExcel = item.exportType?.toLowerCase() === exportType.EXCEL.toLowerCase();
        const isPdf = item.exportType?.toLowerCase() === exportType.PDF.toLowerCase();
        const isP6 = item.exportType?.toLowerCase() === exportType.P6.toLowerCase();
        const isMSP = item.exportType?.toLowerCase() === exportType.MSP.toLowerCase();

        return (
          <div key={item._id} className="flex items-center px-6 py-4 border-b border-[#DCE8F5] last:border-b-0 gap-3">
            {isExcel && <IconComponent name="FileXLSX" className="w-6 h-6 flex-shrink-0" />}
            {isPdf && <IconComponent name="FilePDF" className="w-6 h-6 flex-shrink-0" />}
            {isP6 && <IconComponent name="primaveraP6" className="w-6 h-6 flex-shrink-0" />}
            {isMSP && <IconComponent name="msproject" className="w-6 h-6 flex-shrink-0" />}
            {!isExcel && !isPdf && !isP6 && !isMSP && <IconComponent name="file" className="w-6 h-6 flex-shrink-0" />}
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <Text className="truncate" title={fileName}>
                {fileName}
              </Text>
              <span
                className={`text-xs ${
                  item.exportStatus === fileConversionStatus.ERRORED ? "text-[#D92D20]" : "text-gray-500"
                }`}
              >
                {(() => {
                  if (item.exportStatus === fileConversionStatus.IN_PROGRESS) {
                    return t("Processing your export");
                  } else if (item.exportStatus === fileConversionStatus.ERRORED) {
                    return t("Processing failed");
                  } else {
                    return formattedDate;
                  }
                })()}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {sectionType === "ongoing" && (
                <>
                  {item.exportStatus === fileConversionStatus.IN_PROGRESS && (
                    <IconComponent name="loader-icon" className="w-5 h-5 animate-spin cursor-pointer" />
                  )}

                  {item.exportStatus === fileConversionStatus.ERRORED && (
                    <IconComponent
                      name="reset_template"
                      className="w-5 h-5 cursor-pointer hover:text-blue-600"
                      onClick={() => item.scheduleId && onRetry?.(item._id, item.scheduleId, item.exportType)}
                    />
                  )}

                  {item.exportStatus === fileConversionStatus.ERRORED && (
                    <IconComponent
                      name="delete"
                      className="w-5 h-5 cursor-pointer hover:text-red-600"
                      onClick={() => onDelete?.(item._id)}
                    />
                  )}
                </>
              )}
              {sectionType === "completed" && (
                <IconComponent
                  name="download"
                  className="w-5 h-5 cursor-pointer hover:text-blue-600"
                  onClick={() => onDownload?.(item.awsKey, item.fileName || item.awsKey)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

const ExportSection = memo(function ExportSection({
  title,
  items,
  buttonText,
  sectionType,
  onDelete,
  onRetry,
  onDownload,
}: ExportSectionProps) {
  return (
    <div className="border border-[#DCE8F5] bg-white rounded-md">
      <SectionHeader title={title} buttonText={buttonText} />
      <SectionContent
        items={items}
        sectionType={sectionType}
        onDelete={onDelete}
        onRetry={onRetry}
        onDownload={onDownload}
      />
    </div>
  );
});

function EmptyExportsState() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewAllExports = () => {
    navigate(getAllNotificationPageUrl({ section: "exportedFiles" }));
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full py-16">
      <img src={NoData} alt="No Exports" className="w-64 h-64 mb-6" />
      <Text className="text-xl font-semibold text-[#3e8ab0] mb-4">{t("No Exports Available")}</Text>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleViewAllExports}>
        {t("View all Exports")}
      </ButtonComponent>
    </div>
  );
}

function ExportedFiles({ onCountChange }: ExportedFilesProps) {
  const dispatch = useDispatch();
  const [ongoingData, setOngoingData] = useState<ExportItem[]>([]);
  const [completedData, setCompletedData] = useState<ExportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const skipRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const result = await axiosWrapper.post<DeleteExportDataResponse>(`${getBackendUrl()}/tasks/deleteExportData`, {
        data: {
          exportId: id,
        },
      });

      if (result) {
        setOngoingData((prev) => prev.filter((item: any) => item._id !== id));
        setCompletedData((prev) => prev.filter((item: any) => item._id !== id));

        dispatch(
          showToastNotification({
            content: t("Export deleted successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: t("Export deletion failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while deleting the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(error);
    }
  }, []);

  const handleRetry = useCallback(async (id: string, scheduleId: string, type: string) => {
    try {
      const result = await axiosWrapper.post<ExportScheduleTasksResponse>(
        `${getBackendUrl()}/tasks/exportScheduleTasks?scheduleId=${scheduleId}`,
        {
          _id: id,
          type,
        },
      );

      if (result) {
        setOngoingData((prev) =>
          prev.map((item: any) =>
            item._id === id ? { ...item, exportStatus: fileConversionStatus.IN_PROGRESS } : item,
          ),
        );

        dispatch(
          showToastNotification({
            content: t("Export retry initiated successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: t("Export retry failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while retrying the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(error);
    }
  }, []);

  const handleDownload = useCallback(async (awsKey: string, fileName: string) => {
    try {
      const encodedKey = encodeURIComponent(awsKey);
      const encodedName = encodeURIComponent(fileName);
      const downloadUrl = new URL(
        `${getBackendUrl()}/fileSystemServer/getMailAttachmentFile/${encodedKey}/${encodedName}`,
      );
      const link = document.createElement("a");
      link.href = downloadUrl.toString();
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      dispatch(
        showToastNotification({
          content: t("Export download initiated"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while downloading the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(error);
    }
  }, []);

  const fetchOngoingData = useCallback(async () => {
    try {
      const response = await axiosWrapper.post<GetAllExportDataResponse>(`${getBackendUrl()}/tasks/getAllExportData`, {
        value: {
          skip: 0,
          take: 100,
          statusFilter: [fileConversionStatus.IN_PROGRESS, fileConversionStatus.ERRORED],
        },
      });
      const data = response?.data?.result || [];
      const sortedData = [...data].sort((a: any, b: any) => b.createdAt - a.createdAt);
      setOngoingData(sortedData);
      return response?.data?.count || 0;
    } catch (error) {
      console.error(error);
      Promise.reject(error);
      return 0;
    }
  }, []);

  const fetchCompletedData = useCallback(
    async (isNextPage = false) => {
      if (isFetchingRef.current || (isNextPage && !hasMore)) return 0;
      isFetchingRef.current = true;
      setIsFetching(true);
      try {
        const currentSkip = isNextPage ? skipRef.current : 0;
        const currentTake = isNextPage ? 10 : skipRef.current || 10;
        const response = await axiosWrapper.post<GetAllExportDataResponse>(
          `${getBackendUrl()}/tasks/getAllExportData`,
          {
            value: {
              skip: currentSkip,
              take: currentTake,
              statusFilter: [fileConversionStatus.COMPLETED, fileConversionStatus.DOWNLOADED],
            },
          },
        );
        const data = response?.data?.result || [];
        const apiTotalCount = response?.data?.count;
        const sortedData = [...data].sort((a: any, b: any) => b.createdAt - a.createdAt);

        if (isNextPage) {
          setCompletedData((prev) => [...prev, ...sortedData]);
          skipRef.current += 10;
        } else {
          setCompletedData(sortedData);
          if (skipRef.current === 0) {
            skipRef.current = 10;
          }
        }
        setHasMore(data.length === currentTake);
        return apiTotalCount;
      } catch (error) {
        console.error(error);
        return 0;
      } finally {
        isFetchingRef.current = false;
        setIsFetching(false);
      }
    },
    [hasMore],
  );

  const fetchData = useCallback(
    async (isNextPage = false) => {
      if (!isNextPage) {
        setLoading(true);
      }
      try {
        const [ongoingCount, completedCount] = await Promise.all([fetchOngoingData(), fetchCompletedData(isNextPage)]);
        setTotalCount((ongoingCount || 0) + (completedCount || 0));
      } finally {
        setLoading(false);
      }
    },
    [fetchOngoingData, fetchCompletedData],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting && hasMore && !loading && !isFetchingRef.current) {
          fetchCompletedData(true);
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, fetchCompletedData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData]);

  const hasNoExports = !loading && ongoingData.length === 0 && completedData.length === 0;

  useEffect(() => {
    if (!loading) {
      const count = totalCount !== null ? totalCount : ongoingData.length + completedData.length;
      onCountChange?.(count);
    }
  }, [totalCount, ongoingData.length, completedData.length, onCountChange, loading]);

  return (
    <div className="px-6 pt-3 w-full h-full">
      <div className="flex flex-col gap-4 w-full pb-3 h-full">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <IconComponent name="loader-icon" className="w-8 h-8 animate-spin" />
          </div>
        ) : hasNoExports ? (
          <EmptyExportsState />
        ) : (
          <>
            {ongoingData.length > 0 && (
              <ExportSection
                title="Ongoing"
                items={ongoingData}
                // buttonText="Pause all"
                sectionType="ongoing"
                onDelete={handleDelete}
                onRetry={handleRetry}
                onDownload={handleDownload}
              />
            )}
            {completedData.length > 0 && (
              <ExportSection
                title="Completed"
                items={completedData}
                // buttonText="Clear all"
                sectionType="completed"
                onDelete={handleDelete}
                onRetry={handleRetry}
                onDownload={handleDownload}
              />
            )}
          </>
        )}
        {hasMore && !loading && <div ref={observerRef} className="h-4 w-full" />}
        {isFetching && (
          <div className="flex items-center justify-center py-4">
            <IconComponent name="loader-icon" className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportedFiles;
